import {
  OPTIMIZELY_COOKIE_NAME, TEST_COOKIE_NAME, TOKEN_CLAIMS, TENANT_DOMAIN_RESOLVE_URL,
  LOCALE_RESOLVE_URL,
  EMPTY_IDENTITY
} from '../../common/constants'
import { UrlParam, UserSignInOptions, UserIdentityEvent } from '../../common/types'
import { axiosGet } from './axiosWrapper'

// Known brand/parent domain labels used to determine the cookie-sharing root domain
const allowedParentDomains = ['vistaprint', 'vista', 'proshopnext', 'ourproshop', 'pens']

/**
 * Gets the root domain for cookie sharing.
 * Scans the hostname segments from left to right and, on the first segment that
 * matches a known parent domain label, returns that segment and everything after
 * it (e.g. shop.pro.vistaprint.com -> pro.vistaprint.com). If no label matches,
 * the full hostname is returned.
 */
export const getRootDomain = (): string => {
  const hostname = window?.location?.hostname ?? ''

  if (!hostname) return ''

  const hostSegments = hostname.split('.')

  for (let index = 0; index < hostSegments.length; index += 1) {
    const currentSegment = hostSegments[index]
    if (allowedParentDomains.indexOf(currentSegment) >= 0) {
      return hostSegments.slice(index).join('.')
    }
  }
  return hostname
}

export const setCookie = (name: string, value: string, expires?: Date): void => {
  const domain = getRootDomain()
  const isSecure = window.location?.protocol === 'https:'
  const domainAttr = domain ? `;domain=${domain}` : ''
  let cookieString = `${name}=${value};path=/${domainAttr};SameSite=Lax${isSecure ? ';Secure' : ''}`

  if (expires) {
    cookieString += `;expires=${expires.toUTCString()}`
  }

  document.cookie = cookieString
}

export const getCookie = (name: string, parseJson: boolean = false): any | undefined => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    const raw = parts.pop()?.split(';').shift() ?? ''
    if (!parseJson) {
      return raw
    }
    try {
      return JSON.parse(raw)
    } catch {
      return raw
    }
  }
  return undefined
}

export const deleteCookie = (name: string): void => {
  const domain = getRootDomain()
  // IMPORTANT: To delete a domain-level cookie, you MUST specify the domain
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`
}

export function getWindow (): any {
  return window
}

export function getWindowLocation (): Location {
  return getWindow().location
}

export function isEmployeeConnection (connection: string): boolean {
  return connection.indexOf('-waad') !== -1 || connection.indexOf('-okta') !== -1
}

export function buildPath (
  additionalReturnUrlQueryParams: UrlParam[],
  additionalReturnUrlHashParams: UrlParam[]
): string {
  let pathName = '/'// Default base path

  const queryString = additionalReturnUrlQueryParams
    .map(({ key, value }) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')

  const hashString = additionalReturnUrlHashParams
    .map(({ key, value }) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')

  if (queryString) {
    pathName += `?${queryString}`
  }

  if (hashString) {
    pathName += `#${hashString}`
  }

  return pathName.replace(/^\//, '') // Ensure it integrates well with `location.origin`
}

export function buildWAuth (options: UserSignInOptions, returnUrl: string, canonicalId: string | null, token: string | undefined, culture: string): string {
  const state = {
    ...options,
    redirectUrl: returnUrl,
    originUrl: getWindowLocation()?.href?.split('?')[0],
    visit: getCookie('visit'),
    ga: getCookie('_ga'),
    optimizelyId: getCookie(TEST_COOKIE_NAME) || getCookie(OPTIMIZELY_COOKIE_NAME),
    anonymousId: canonicalId,
    anonReferenceToken: token,
    culture,
    guestReturnUrl: options.guestReturnUrl ? options.guestReturnUrl : returnUrl,
    site: options.site || defaultSite()
  }
  return btoa(JSON.stringify(state))
}

export function replaceHistoryState (): void {
  // Create a URL object from the current URL
  const url = new URL(window.location.href)
  if (url.searchParams.has('code') && url.searchParams.has('state')) {
    // Remove each specified query parameter
    url.searchParams.delete('code')
    url.searchParams.delete('state')

    window.history.replaceState(window.history.state, '', url.href)
  }
}

function defaultSite (): string {
  const { host = '' } = window.location
  const tokens = host.split('.')

  const domainMapping: Record<string, string> = {
    promotique: 'Promotique',
    '99designs': '99designs',
    vistacreate: 'vistacreate',
  }

  return tokens.find((token) => domainMapping[token]) ? domainMapping[tokens.find((token) => domainMapping[token])!] : 'Vistaprint'
}

export function buildRedirectUrl (
  location: Location,
  additionalQueryParams: UrlParam[] = [],
  additionalHashParams: UrlParam[] = []
): string {
  if (additionalQueryParams.length === 0 &&
    additionalHashParams.length === 0 &&
    location.search.indexOf('askUserToLogin') < 0) {
    return location.href
  }

  let searchParam = ''
  if (location.search) {
    const urlParts = location.search.split('?')
    if (urlParts.length >= 2) {
      const prefix = encodeURIComponent('askUserToLogin') + '='
      const pars = urlParts[1].split(/[&;]/g).filter((p) => p.lastIndexOf(prefix, 0) === -1)
      searchParam = urlParts[0] + (pars.length > 0 ? '?' + pars.join('&') : '')
    } else {
      searchParam = location.search
    }
  }

  let url = location.origin
  if (location.pathname) url += location.pathname
  if (searchParam) url += searchParam

  if (additionalQueryParams.length > 0) {
    url += (searchParam ? '&' : '?') + additionalQueryParams
      .map(({ key, value }) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')
  }

  if (location.hash) url += location.hash

  if (additionalHashParams.length > 0) {
    url += (searchParam ? '&' : '#') + additionalHashParams
      .map(({ key, value }) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')
  }

  return url
}

export function transformAuthIdentityEventToUserIdentityEvent (identity: any): any {
  if (!identity || Object.keys(identity).length === 0) return EMPTY_IDENTITY
  return {
    ...identity,
    accessToken: null,
    recentAnonymousIds: identity.profile ? identity.profile[TOKEN_CLAIMS.WAS_TOKEN_CLAIM] : [],
    authorizationHeader: identity.authorization ? `${identity.authorization.type} ${identity.authorization.token}` : null,
    referenceToken: identity.authorization?.token ?? null
  }
}

export function trackSignIn (caller: string | undefined, callback?: () => void): void {
  trackEvent('Sign-in Flow Initiated', caller)

  // Ensure tracking event had enought time to be sent.
  if (callback) {
    setTimeout(callback, 300)
  }
}

export async function getDomain (tenant: string, locale: string): Promise<string> {
  interface DomainResponse {
    domain: string
  }
  try {
    const response = await axiosGet<DomainResponse>({ url: `${TENANT_DOMAIN_RESOLVE_URL}?tenant=${tenant}&locale=${locale}` })
    return response.domain
  } catch (error) {
    console.error(`Could not resolve domain for tenant: ${tenant}, locale: ${locale}`)
    throw error
  }
}

export async function getHomepageUrl (tenant: string, locale: string): Promise<string> {
  interface LocaleResponse {
    url: string
  }
  try {
    const domain = await getDomain(tenant, locale)
    const { url: localePath } = await axiosGet<LocaleResponse>({ url: `${LOCALE_RESOLVE_URL}/${tenant}/${locale}/other:home?requestor=vp/auth` })
    return domain ? `https://${domain}${localePath}` : ''
  } catch (error) {
    console.error(`Could not resolve homepage URL for tenant: ${tenant}, locale: ${locale}`)
    const location = getWindowLocation()
    return location.origin
  }
}

function trackEvent (eventName: string, caller: string | undefined): void {
  const window = getWindow()
  if (!window || !window.tracking || typeof window.tracking.track !== 'function') {
    console.warn('Tracking-js not found. Cannot track sign-in event.')
    return
  }

  window.tracking.track(eventName, { caller })
}

export function redirectTo (location: string): void {
  getWindowLocation().href = location
}

export function isNonEmptyString (value: any): value is string {
  return typeof value === 'string' && value !== ''
}

export function removeCareAgentParam (href: string): string {
  const url = new URL(href)
  if (url.searchParams.has('careAgent')) {
    url.searchParams.delete('careAgent')
  }
  return url.href
}

export function checkIsInternalUser (identity: UserIdentityEvent): boolean {
  const profile = identity.profile as Record<string, any>
  const cimpressInternal = profile && profile[TOKEN_CLAIMS.CIMPRESS_INTERNAL_CLAIM]

  // this logic to support agents at external organizations
  // make sure they're not a customer and that they have the correct canonical ID
  const validCanonicalId = profile && hasValidCanonicalId(profile[TOKEN_CLAIMS.CANONICAL_ID_TOKEN_CLAIM])
  const isCustomer = profile && profile[TOKEN_CLAIMS.CUSTOMER_CLAIM]

  return (validCanonicalId && !isCustomer) || cimpressInternal
}

function hasValidCanonicalId (canonicalIdClaim: undefined | string | []) {
  if (!canonicalIdClaim || typeof canonicalIdClaim !== 'string') return false
  return (
    canonicalIdClaim.toLowerCase().endsWith('@vista.com') ||
    canonicalIdClaim.toLowerCase().endsWith('@vistaprint.com') ||
    // It appears these emails are used by external agents.
    // For more details, see: https://vistaprint.atlassian.net/browse/PLT-1451
    canonicalIdClaim.toLowerCase().endsWith('@affinityx.com')
  )
}

export function isOAuthAuthCodeRedirect (): boolean {
  try {
    const search = getWindowLocation().search
    if (isNonEmptyString(search)) {
      const urlParams = new URLSearchParams(search)
      const code = urlParams.get('code')
      const state = urlParams.get('state')
      return isNonEmptyString(code) && isNonEmptyString(state)
    }
    return false
  } catch (err) {
    return false
  }
}
